import React from "react";
import {
  PeriodReportData,
  ReportCategory,
} from "@/api/fetchQuickBooksPeriodReport/types";

interface PeriodReportTableProps {
  report: PeriodReportData | null;
}

interface CategoryRowProps {
  category: ReportCategory;
  level: number;
  isHeader?: boolean;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatPercentage = (percent: number): string => {
  return `${(percent * 100).toFixed(2)}%`;
};

const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  level,
  isHeader = false,
}) => {
  const paddingLeft = level * 24;

  return (
    <>
      <tr
        className={`
        ${isHeader ? "bg-gray-100 font-semibold" : "hover:bg-gray-50"}
        ${level === 0 ? "border-t-2 border-gray-300" : ""}
      `}
      >
        <td className="py-2 px-4" style={{ paddingLeft: `${paddingLeft}px` }}>
          {category.name}
          {category.accountCode && (
            <span className="text-gray-500 ml-2 text-sm">
              ({category.accountCode})
            </span>
          )}
        </td>
        <td className="py-2 px-4 text-right">
          {formatCurrency(category.amount)}
        </td>
        <td className="py-2 px-4 text-right">
          {formatPercentage(category.percentOfRevenue)}
        </td>
      </tr>
      {category.subcategories.map(
        (subcategory: ReportCategory, index: number) => (
          <CategoryRow
            category={subcategory}
            key={`${subcategory.name}-${index}`}
            level={level + 1}
          />
        ),
      )}
    </>
  );
};

export const PeriodReportTable: React.FC<PeriodReportTableProps> = ({
  report,
}) => {
  if (!report) {
    return null;
  }

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Account
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              % of Revenue
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          <CategoryRow category={report.revenue} isHeader level={0} />
          <CategoryRow category={report.costOfGoodsSold} isHeader level={0} />
          <CategoryRow category={report.grossProfit} isHeader level={0} />
          <CategoryRow category={report.operatingExpenses} isHeader level={0} />
          <CategoryRow category={report.operatingIncome} isHeader level={0} />
          <CategoryRow
            category={report.otherIncomeExpenses}
            isHeader
            level={0}
          />
          <CategoryRow category={report.netIncome} isHeader level={0} />
        </tbody>
      </table>
    </div>
  );
};
